import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Card, Row, Col, Statistic, Result, message, Typography, Empty, Radio } from 'antd';
import { RocketOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getCiclos,
  getResumen,
  type ReconciliacionCiclo,
  type ReconciliacionResumen,
} from '../../api/reconciliacionApi';
import MotorProgressOverlay from './MotorProgressOverlay';

const { Text } = Typography;

interface HistoricoTabProps {
  ciclos: ReconciliacionCiclo[];
  selectedRecIds: number[];
  onSelectionChange: (ids: number[]) => void;
  onCiclosReload: (ciclos: ReconciliacionCiclo[]) => void;
}

const HistoricoTab: React.FC<HistoricoTabProps> = ({ ciclos, selectedRecIds, onSelectionChange, onCiclosReload }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [motorVisible, setMotorVisible] = useState(false);
  const [motorOpcion, setMotorOpcion] = useState('titulares');
  const opcionRef = useRef('titulares');

  // Modal de detalle
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedCiclo, setSelectedCiclo] = useState<ReconciliacionCiclo | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resumen, setResumen] = useState<ReconciliacionResumen | null>(null);

  const loadCiclos = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await getCiclos();
      onCiclosReload(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (ciclo: ReconciliacionCiclo) => {
    setSelectedCiclo(ciclo);
    setDetailVisible(true);
    setDetailLoading(true);
    try {
      const data = await getResumen(ciclo.id);
      setResumen(data);
    } catch {
      message.error('Error al cargar el detalle del ciclo');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleLanzarMotor = () => {
    opcionRef.current = 'titulares';
    Modal.confirm({
      title: 'Reconciliación con datos oficiales (CU2)',
      content: (
        <div>
          <p>Se lanzará una nueva ejecución del motor de reconciliación datos CU2-Gescom, para detectar: nuevos farmacéuticos, altas/bajas de farmacias, cambios en titularidad, cambios de empleados y posibles traslados de farmacias.</p>
          <p>La operación puede tardar unos minutos.</p>
          <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
            <Radio.Group
              defaultValue="titulares"
              size="small"
              onChange={(e) => { opcionRef.current = e.target.value; }}
            >
              <Radio value="titulares">Sólo titulares</Radio>
              <Radio value="todos">Todos los colegiados</Radio>
            </Radio.Group>
          </div>
        </div>
      ),
      okText: 'Lanzar',
      cancelText: 'Cancelar',
      onOk: () => {
        setMotorOpcion(opcionRef.current);
        setMotorVisible(true);
      },
    });
  };

  const columns: ColumnsType<ReconciliacionCiclo> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Fecha y hora',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (val: string) => new Date(val).toLocaleString('es-ES'),
      width: 180,
    },
    {
      title: 'Modo',
      dataIndex: 'modo',
      key: 'modo',
      width: 100,
      render: (val: string) => <Text style={{ textTransform: 'capitalize' }}>{val}</Text>,
    },
    {
      title: 'Opción',
      dataIndex: 'opcion',
      key: 'opcion',
      width: 110,
      render: (val: string) => <Text style={{ textTransform: 'capitalize' }}>{val || 'titulares'}</Text>,
    },
    {
      title: 'Farmacias analizadas',
      dataIndex: 'farmaciasAnalizadas',
      key: 'farmaciasAnalizadas',
      width: 160,
      align: 'right',
    },
    {
      title: 'Operaciones',
      dataIndex: 'totalOperaciones',
      key: 'totalOperaciones',
      width: 120,
      align: 'right',
    },
    {
      title: 'Excepciones',
      dataIndex: 'totalExcepciones',
      key: 'totalExcepciones',
      width: 120,
      align: 'right',
      render: (val: number) => <Text type={val > 0 ? 'danger' : undefined}>{val}</Text>,
    },
    {
      title: 'Invariantes',
      dataIndex: 'totalInvariantes',
      key: 'totalInvariantes',
      width: 120,
      align: 'right',
      render: (val: number) => <Text type={val > 0 ? 'danger' : undefined}>{val}</Text>,
    },
  ];

  if (error) {
    return (
      <Result
        status="error"
        title="Error al cargar el histórico"
        extra={<Button type="primary" onClick={loadCiclos} icon={<ReloadOutlined />}>Reintentar</Button>}
      />
    );
  }

  const resumenAmbitos = resumen ? [
    { label: 'Farmacias', total: resumen.totalFarmacias, pending: resumen.farmaciasPorEstado[0] || 0 },
    { label: 'Farmacéuticos', total: resumen.totalFarmaceuticos, pending: resumen.farmaceuticosPorEstado[0] || 0 },
    { label: 'Transmisiones', total: resumen.totalTransmisiones, pending: resumen.transmisionesPorEstado[0] || 0 },
    { label: 'Empleados', total: resumen.totalEmpleados, pending: resumen.empleadosPorEstado[0] || 0 },
    { label: 'Traslados', total: resumen.totalTraslados, pending: resumen.trasladosPorEstado[0] || 0 },
    { label: 'Incidencias', total: resumen.totalIncidencias, pending: resumen.incidenciasPorEstado[0] || 0 },
  ] : [];

  const rowSelection = {
    selectedRowKeys: selectedRecIds,
    onChange: (keys: React.Key[]) => {
      onSelectionChange(keys.map(k => Number(k)));
    },
  };

  return (
    <div>
      {/* Cabecera */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Text type="secondary">
          {ciclos.length} ejecuciones registradas — {selectedRecIds.length} seleccionada{selectedRecIds.length !== 1 ? 's' : ''}
        </Text>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button icon={<ReloadOutlined />} onClick={loadCiclos} loading={loading}>Actualizar</Button>
          <Button icon={<RocketOutlined />} onClick={handleLanzarMotor}>
            Lanzar conciliación CU2
          </Button>
        </div>
      </div>

      {/* Tabla */}
      {ciclos.length === 0 && !loading ? (
        <Empty description="Aún no se ha ejecutado el motor">
          <Button type="primary" icon={<RocketOutlined />} onClick={handleLanzarMotor}>
            Lanzar conciliación CU2
          </Button>
        </Empty>
      ) : (
        <Table
          dataSource={ciclos.map(c => ({ ...c, key: c.id }))}
          columns={columns}
          rowSelection={rowSelection}
          loading={loading}
          pagination={false}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
          size="middle"
        />
      )}

      {/* Modal de detalle */}
      <Modal
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        title={selectedCiclo ? `Ejecución ${new Date(selectedCiclo.timestamp).toLocaleString('es-ES')} — ${selectedCiclo.modo}` : ''}
        width={700}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Cargando detalle...</div>
        ) : resumen ? (
          <div>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={8}><Statistic title="Farmacias analizadas" value={selectedCiclo?.farmaciasAnalizadas || 0} /></Col>
              <Col span={8}><Statistic title="Operaciones" value={selectedCiclo?.totalOperaciones || 0} /></Col>
              <Col span={8}><Statistic title="Excepciones" value={selectedCiclo?.totalExcepciones || 0} /></Col>
            </Row>

            <Card title="Desglose por ámbito" size="small">
              <Row gutter={[16, 12]}>
                {resumenAmbitos.map(a => (
                  <Col span={8} key={a.label}>
                    <div style={{ padding: '8px 0' }}>
                      <Text strong>{a.label}</Text>
                      <div>
                        <Text type="secondary">Total: {a.total}</Text>
                        {' · '}
                        <Text type={a.pending > 0 ? 'warning' : 'success'}>
                          Pendientes: {a.pending}
                        </Text>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </div>
        ) : (
          <Empty description="Sin datos" />
        )}
      </Modal>

      {/* Overlay de progreso del motor */}
      <MotorProgressOverlay
        visible={motorVisible}
        modo="semanal"
        opcion={motorOpcion}
        onClose={() => setMotorVisible(false)}
        onCompleted={loadCiclos}
      />
    </div>
  );
};

export default HistoricoTab;
